import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useModules, MODULE_CATALOGUE, METHODOLOGY_PACKS } from '../../context/ModuleContext';
import { Card } from '../../design-system/Card';
import { Input } from '../../design-system/Input';
import { Button } from '../../design-system/Button';
import { Badge } from '../../design-system/Badge';
import {
  Layers,
  ArrowRight,
  Check,
  Sparkles,
  Sliders
} from 'lucide-react';

export const OnboardingPage = ({ onComplete }) => {
  const { user } = useAuth();
  const { createWorkspace, createProject, addTask } = useWorkspace();
  const { applyMethodology, enabledModules, toggleModule } = useModules();

  const [mode, setMode] = useState('prebuilt'); // 'prebuilt' or 'custom'
  const [selectedTemplate, setSelectedTemplate] = useState('software');
  const [workspaceName, setWorkspaceName] = useState(
    user?.name ? `${user.name}'s Workspace` : 'My Forma Workspace'
  );
  const [projectName, setProjectName] = useState('Core Engineering V1');

  // Categories for Custom Setup
  const categories = [
    { id: 'Work Views', title: 'Core Work Views', desc: 'Essential visual task tracking and timeline layouts' },
    { id: 'Agile Software', title: 'Agile & Execution', desc: 'Sprint iterations, backlogs, and workflow automation' },
    { id: 'Specialized', title: 'Specialized Industry Modules', desc: 'Domain-specific toolsets for Game Dev, Hardware BOM, and Research' },
    { id: 'Knowledge', title: 'Knowledge & Sharing', desc: 'Wikis, technical documentation, and public portals' }
  ];

  const handleLaunchPrebuilt = (templateId) => {
    applyMethodology(templateId);
    const template = METHODOLOGY_PACKS.find((p) => p.id === templateId);

    // 1. Create Workspace
    const ws = createWorkspace({
      name: workspaceName || `${user?.name || 'My'}'s Workspace`,
      slug: (workspaceName || 'workspace').toLowerCase().replace(/[^a-z0-9]+/g, '-')
    });

    // 2. Create Project
    const prj = createProject({
      name: projectName || `${template?.name || 'Primary'} Project`,
      key: (template?.id || 'PRJ').substring(0, 3).toUpperCase(),
      workspaceId: ws.id,
      category: template?.name || 'General',
      lead: user?.name || 'Workspace Owner'
    });

    // 3. Add Welcome Tasks
    addTask({
      projectId: prj.id,
      title: 'Welcome to your Forma workspace! 👋',
      description: 'Click this task to customize your description, checklists, and labels.',
      status: 'in_progress',
      priority: 'high',
      assignee: { name: user?.name || 'You', avatar: user?.avatar || '' }
    });

    addTask({
      projectId: prj.id,
      title: `Explore the ${template?.name || 'prebuilt'} methodology views`,
      description: 'Use the left sidebar or keyboard shortcuts (Cmd+K) to switch views.',
      status: 'todo',
      priority: 'medium',
      assignee: { name: user?.name || 'You', avatar: user?.avatar || '' }
    });

    onComplete();
  };

  const handleLaunchCustom = () => {
    // 1. Create Workspace
    const ws = createWorkspace({
      name: workspaceName || `${user?.name || 'My'}'s Workspace`,
      slug: (workspaceName || 'workspace').toLowerCase().replace(/[^a-z0-9]+/g, '-')
    });

    // 2. Create Project
    const prj = createProject({
      name: projectName || 'Custom Project',
      key: (projectName || 'PRJ').substring(0, 3).toUpperCase().replace(/[^A-Z]/g, 'PRJ') || 'PRJ',
      workspaceId: ws.id,
      category: 'Custom Workspace',
      lead: user?.name || 'Workspace Owner'
    });

    // 3. Add Starter Task
    addTask({
      projectId: prj.id,
      title: 'Configure custom project roadmap',
      description: 'Your custom module stack has been activated. Start building tasks!',
      status: 'todo',
      priority: 'medium',
      assignee: { name: user?.name || 'You', avatar: user?.avatar || '' }
    });

    onComplete();
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--bg-app)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '36px 20px'
      }}
    >
      <div style={{ width: '100%', maxWidth: '820px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* Brand Header */}
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              width: '46px',
              height: '46px',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--accent-container)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              boxShadow: 'var(--shadow-raised-sm)',
              marginBottom: '12px'
            }}
          >
            <Layers size={28} strokeWidth={2.5} />
          </div>
          <h1 style={{ fontSize: '28px', fontWeight: '800' }}>Setup Your Forma Workspace</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginTop: '4px' }}>
            Welcome <strong>{user?.name || 'Builder'}</strong>! Choose a discipline template or build your custom module stack.
          </p>
        </div>

        {/* Mode Selector Switcher */}
        <div
          style={{
            display: 'flex',
            backgroundColor: 'var(--surface-recessed)',
            boxShadow: 'var(--shadow-recessed-sm)',
            borderRadius: 'var(--radius-md)',
            padding: '4px',
            maxWidth: '380px',
            margin: '0 auto',
            width: '100%'
          }}
        >
          <button
            onClick={() => setMode('prebuilt')}
            style={{
              flex: 1,
              padding: '8px 14px',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              backgroundColor: mode === 'prebuilt' ? 'var(--surface-card)' : 'transparent',
              boxShadow: mode === 'prebuilt' ? 'var(--shadow-raised-sm)' : 'none',
              color: mode === 'prebuilt' ? 'var(--accent-teal)' : 'var(--text-secondary)',
              fontSize: '13px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.15s'
            }}
          >
            <Sparkles size={15} />
            <span>Pre-built Templates</span>
          </button>

          <button
            onClick={() => setMode('custom')}
            style={{
              flex: 1,
              padding: '8px 14px',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              backgroundColor: mode === 'custom' ? 'var(--surface-card)' : 'transparent',
              boxShadow: mode === 'custom' ? 'var(--shadow-raised-sm)' : 'none',
              color: mode === 'custom' ? 'var(--accent-teal)' : 'var(--text-secondary)',
              fontSize: '13px',
              fontWeight: '700',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.15s'
            }}
          >
            <Sliders size={15} />
            <span>Custom Stack</span>
          </button>
        </div>

        {/* PREBUILT TEMPLATES MODE */}
        {mode === 'prebuilt' && (
          <Card padding="28px" className="animate-fade-in">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: '700' }}>Select a Discipline Template</h3>
                <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                  Each template configures the recommended modules. You can add or remove modules anytime.
                </p>
              </div>
              <Badge variant="teal" size="sm">6 Templates</Badge>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
                gap: '14px',
                marginBottom: '24px'
              }}
            >
              {METHODOLOGY_PACKS.map((pack) => {
                const isSelected = selectedTemplate === pack.id;
                return (
                  <div
                    key={pack.id}
                    onClick={() => {
                      setSelectedTemplate(pack.id);
                      setProjectName(`${pack.name} V1`);
                    }}
                    style={{
                      padding: '16px',
                      borderRadius: 'var(--radius-lg)',
                      backgroundColor: isSelected ? 'var(--surface-recessed)' : 'var(--surface-card)',
                      boxShadow: isSelected ? 'var(--shadow-recessed)' : 'var(--shadow-raised-sm)',
                      border: isSelected ? '2px solid var(--accent-teal)' : '1px solid var(--border-subtle)',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      transition: 'all 0.18s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span
                        className="material-symbols-outlined"
                        style={{
                          fontSize: '24px',
                          color: isSelected ? 'var(--accent-teal)' : 'var(--text-secondary)'
                        }}
                      >
                        {pack.icon}
                      </span>
                      {isSelected && <Check size={18} color="var(--accent-teal)" />}
                    </div>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>
                      {pack.name}
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>
                      {pack.description}
                    </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', marginTop: 'auto', paddingTop: '6px' }}>
                      {pack.recommendedModules.map((mId) => (
                        <span
                          key={mId}
                          style={{
                            fontSize: '10px',
                            fontWeight: '600',
                            backgroundColor: 'var(--surface-alt)',
                            padding: '2px 6px',
                            borderRadius: '3px',
                            color: 'var(--text-muted)'
                          }}
                        >
                          {mId.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Workspace & Project Name Fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '20px' }}>
              <Input
                label="Workspace Name"
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
                placeholder="e.g., Acme Studios, Personal Projects"
              />
              <Input
                label="Primary Project Name"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="e.g., Core Engine, Level 1, Prototype V1"
              />
            </div>

            <Button
              variant="primary"
              fullWidth
              size="lg"
              onClick={() => handleLaunchPrebuilt(selectedTemplate)}
              icon={<Sparkles size={18} />}
            >
              Launch Workspace with {METHODOLOGY_PACKS.find((p) => p.id === selectedTemplate)?.name} Template
            </Button>
          </Card>
        )}

        {/* CUSTOM STACK BY CATEGORY MODE */}
        {mode === 'custom' && (
          <Card padding="28px" className="animate-fade-in">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: '700' }}>Build Your Custom Stack</h3>
                <p style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                  Select the exact components you need grouped by category.
                </p>
              </div>
              <Badge variant="teal" size="sm">{enabledModules.length} Modules Selected</Badge>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '20px' }}>
              <Input
                label="Workspace Name"
                value={workspaceName}
                onChange={(e) => setWorkspaceName(e.target.value)}
                placeholder="e.g., Acme Studios"
              />
              <Input
                label="First Project Name"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="e.g., Main Platform"
              />
            </div>

            {/* Categories and Component Cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '24px' }}>
              {categories.map((cat) => {
                const catModules = MODULE_CATALOGUE.filter((m) => {
                  if (cat.id === 'Work Views') return m.category === 'Work Views' || m.category === 'Planning';
                  if (cat.id === 'Agile Software') return m.category === 'Agile Software' || m.category === 'Automations';
                  if (cat.id === 'Specialized') return m.category === 'Specialized';
                  return m.category === 'Knowledge' || m.category === 'Portals';
                });

                return (
                  <div
                    key={cat.id}
                    style={{
                      backgroundColor: 'var(--surface-recessed)',
                      padding: '16px',
                      borderRadius: 'var(--radius-lg)',
                      border: '1px solid var(--border-subtle)'
                    }}
                  >
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ fontSize: '14px', fontWeight: '800', color: 'var(--text-primary)' }}>
                        {cat.title}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        {cat.desc}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '10px' }}>
                      {catModules.map((mod) => {
                        const isEnabled = enabledModules.includes(mod.id);
                        return (
                          <div
                            key={mod.id}
                            onClick={() => toggleModule(mod.id)}
                            style={{
                              padding: '10px 12px',
                              backgroundColor: isEnabled ? 'var(--surface-card)' : 'transparent',
                              boxShadow: isEnabled ? 'var(--shadow-raised-sm)' : 'none',
                              border: isEnabled ? '1.5px solid var(--accent-teal)' : '1px solid var(--border-subtle)',
                              borderRadius: 'var(--radius-md)',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: '8px',
                              transition: 'all 0.15s'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span
                                className="material-symbols-outlined"
                                style={{
                                   fontSize: '18px',
                                   color: isEnabled ? 'var(--accent-teal)' : 'var(--text-muted)'
                                }}
                              >
                                {mod.icon}
                              </span>
                              <div>
                                <div style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-primary)' }}>
                                  {mod.name}
                                </div>
                              </div>
                            </div>

                            <span
                              style={{
                                fontSize: '11px',
                                fontWeight: '700',
                                color: isEnabled ? 'var(--accent-teal)' : 'var(--text-muted)'
                              }}
                            >
                              {isEnabled ? '✓' : '+'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <Button
              variant="primary"
              fullWidth
              size="lg"
              onClick={handleLaunchCustom}
              icon={<ArrowRight size={18} />}
            >
              Create Custom Workspace ({enabledModules.length} Modules)
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};
